/**
 * FloatingLanguageToggle — collapsible EN|ES pill anchored to the
 * top-right of the viewport. Collapsed state is a small globe button so
 * it never overlaps content; expanded shows the full pill. Preference
 * is persisted so the user doesn't have to re-collapse on every page.
 *
 * Rendered from App.js so it appears on every route.
 */
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Globe, X } from "lucide-react";
import LanguageToggle from "@/components/LanguageToggle";

const COLLAPSE_KEY = "glpflo_lang_toggle_collapsed_v1";

// Employee / staff portals never need the floating translator — they're
// English-only internal workflows and the pill overlaps dashboard chrome.
const STAFF_PREFIXES = ["/admin", "/prescriber", "/cliniclogin", "/doctordoctor"];

export default function FloatingLanguageToggle() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* noop */ }
  }, [collapsed]);

  const path = location.pathname || "";
  if (STAFF_PREFIXES.some((p) => path === p || path.startsWith(p + "/"))) {
    return null;
  }

  return (
    <div
      className="fixed top-20 right-4 z-[90] pointer-events-none"
      data-testid="floating-language-toggle"
    >
      <div className="pointer-events-auto flex items-center gap-1">
        {collapsed ? (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            aria-label="Open language selector"
            className="w-9 h-9 rounded-full border-2 border-purple-300 bg-white shadow-md hover:shadow-lg transition flex items-center justify-center"
            data-testid="language-toggle-expand"
            translate="no"
          >
            <Globe className="w-4 h-4 text-purple-500" />
          </button>
        ) : (
          <>
            <LanguageToggle />
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse language selector"
              className="w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50 flex items-center justify-center"
              data-testid="language-toggle-collapse"
              translate="no"
            >
              <X className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
